<template>
  <div id="wrap">
    <Header
      :menus="topMenus"
      :selected-menu-id="selectedMenuId"
      :current-menu="currentMenu"
      @menu-change="handleMenuChange"
    />

    <div id="container">
      <Side
        :menus="sideMenus"
        :current-menu="currentMenu"
        :title="selectedTopMenuName"
      />
      <div id="content" class="cont_wrap">
        <TopBar type="sub" />
        <div class="content">
          <SubHeader
            :title="currentTitle"
            :breadcrumbs="currentBreadcrumbs"
          />
          <TopNotice :visible="noticeVisible" :message="noticeMessage" @close="hideNotice"/>

          <!--TabGroup-->
          
          <!--
            메뉴 컨텐츠상단/하단 (MENU_CONTS_T / MENU_CONTS_B)
            - 관리자 메뉴관리에서 입력한 HTML이 렌더링되는 영역
            - 모든 메뉴 유형(BBS, INPRG, CONTENT, LINK 등)에 공통 적용
            - 레거시: content.css(약 2,900줄) + common.css 유틸리티 클래스 의존
              (.fc01~09 글자색, .bc01~09 배경색, .tal/.tac/.tar 정렬, .fwb 볼드 등)
            - 이관 데이터가 레거시 클래스를 사용할 경우 별도 CSS 포함 필요
            - 신규 작성 시 인라인 스타일 권장
          -->
          <!-- 메뉴 컨텐츠상단 -->
          <div v-if="currentMenu?.menuContsT" v-dompurify-html="currentMenu.menuContsT" class="menu-conts-top"></div>
          <slot />
          <!-- 메뉴 컨텐츠하단 -->
          <div v-if="currentMenu?.menuContsB" v-dompurify-html="currentMenu.menuContsB" class="menu-conts-bottom"></div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, provide } from 'vue'
import { Side, SubHeader, TopBar, Header, Footer } from '@/components/layout'
import { useApiMenu } from '@/composables/useApiMenu'
import { provideMenuContext } from '@/composables/useMenuContext'
import { useCurrentMenu } from '@/composables/useCurrentMenu'
import TopNotice from '@/components/feedback/TopNotice.vue'
import { useAutoDismissNotice } from '@/composables/useAutoDismissNotice'
import { useRouter, useRoute } from 'vue-router'
import { useInstSysSetSession } from '@/composables/useInstSysSetSession'

const route = useRoute()
const router = useRouter()
const { getInstSysSetMap } = useInstSysSetSession()

const instSysSetMap = ref({})
const refreshInstSysSetMap = () => {
  instSysSetMap.value = getInstSysSetMap()
}

provide('instSysSetMap', instSysSetMap)
provide('getInstSysSetValue', (sysStngKey, defaultValue = '') => {
  const map = instSysSetMap.value || {}
  return Object.prototype.hasOwnProperty.call(map, sysStngKey) ? map[sysStngKey] : defaultValue
})

// 상단 알림 UI 제어
const { noticeVisible, noticeMessage, showNotice, hideNotice } = useAutoDismissNotice()
provide('topNotice', {
  showNotice,
  hideNotice
})

defineOptions({ name: 'DefaultLayout' })

provideMenuContext()

const DOMAIN_ID = import.meta.env.VITE_DOMAIN_ID
const selectedMenuId = ref(null)

const {
  loadAllMenuData,
  getRootMenus,
  getSubTreeByParent,
  getMenuBreadcrumb
} = useApiMenu()

// 여기서만 useCurrentMenu 사용
const { currentMenu, currentTitle, currentBreadcrumbs } = useCurrentMenu()


const APP_NAME = '[관리자] 대구광역시 통합예약시스템'
watch(
  () => [route.fullPath, currentTitle.value],
  () => {
    const t = (currentTitle.value || '').trim()
    document.title = t ? `${t} | ${APP_NAME}` : APP_NAME
  },
  { immediate: true }
)

const topMenus = computed(() => getRootMenus(DOMAIN_ID))

const selectedTopMenu = computed(() => {
  return topMenus.value.find(m => m.menuId === selectedMenuId.value) || null
})

const selectedTopMenuName = computed(() => {
  return selectedTopMenu.value?.menuNm || ''
})

const sideMenus = computed(() => {
  if (!selectedMenuId.value) return []
  const tree = getSubTreeByParent(selectedMenuId.value)
  return tree
})

const handleMenuChange = (menuId) => {
  selectedMenuId.value = menuId
}

const updateSelectedTopMenuByCurrentMenu = () => {
  const menu = currentMenu.value
  const roots = getRootMenus(DOMAIN_ID)

  if (menu) {
    // 현재 메뉴의 경로를 기반으로 최상위 메뉴 찾기
    const path = getMenuBreadcrumb(menu.menuId) || []
    // 경로의 첫 번째(루트)가 최상위 메뉴
    const root = path.length > 0 ? path[0] : menu
    
    // 루트 메뉴의 menuId로 selectedMenuId 설정
    if (root.menuId) {
      selectedMenuId.value = root.menuId
    }
  } else if (!selectedMenuId.value && roots.length > 0) {
    selectedMenuId.value = roots[0].menuId
  }
}

onMounted(async () => {
  refreshInstSysSetMap()
  window.addEventListener('inst-sys-set-updated', refreshInstSysSetMap)
  const data = await loadAllMenuData(DOMAIN_ID)
  updateSelectedTopMenuByCurrentMenu()
})

onUnmounted(() => {
  window.removeEventListener('inst-sys-set-updated', refreshInstSysSetMap)
})

watch(
  () => currentMenu.value,
  () => {
    updateSelectedTopMenuByCurrentMenu()
  }
)
</script>

